import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../lib/realtime';
import { useReassignScheduleEntry } from '../../hooks/useReassignScheduleEntry';
import { lookupErrorCopy } from '../../lib/errorMap';
import { log as logger } from '../../lib/logger';
import type { WorkOrder } from '../../types';

async function log(biz: string, type: string, desc: string, eId: string, uid: string) {
  try { await supabase.from('activity_log').insert({ business_id: biz, activity_type: type, description: desc, entity_type: 'work_order', entity_id: eId, logged_by: uid }); } catch { /* */ }
}

async function findEntryIdForJob(jobId: string, businessId: string): Promise<string | null> {
  const { data } = await supabase.from('schedule_entries')
    .select('id').eq('job_id', jobId).eq('business_id', businessId).limit(1).maybeSingle();
  return data?.id ?? null;
}

export interface DispatchJob extends WorkOrder { assigned_name?: string; assigned_phone?: string; }
export interface CrewMember { id: string; first_name: string; last_name: string; role: string; employment_type: string; phone: string | null; crewStatus: string; currentJobTitle?: string; nextTime?: string; }
export interface DispatchSummary { queueCount: number; activeCount: number; completedCount: number; crewAvailable: number; crewTotal: number; }

export function useDispatch(businessId: string | undefined) {
  const [queue, setQueue] = useState<DispatchJob[]>([]);
  const [active, setActive] = useState<DispatchJob[]>([]);
  const [completedToday, setCompletedToday] = useState(0);
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const reassignEntry = useReassignScheduleEntry();

  const today = new Date().toISOString().split('T')[0];

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true); setError(null);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000);
    try {
      const [jobsRes, usersRes, schedRes] = await Promise.all([
        supabase.from('work_orders').select('*').eq('business_id', businessId)
          .or(`scheduled_date.eq.${today},scheduled_date.lt.${today},scheduled_date.is.null`)
          .not('status', 'in', '(completed,cancelled)')
          .order('created_at', { ascending: false }).limit(200).abortSignal(ctrl.signal),
        supabase.from('users').select('id, first_name, last_name, role, employment_type, phone')
          .eq('business_id', businessId).eq('status', 'active')
          .in('employment_type', ['employee', 'contractor']).abortSignal(ctrl.signal),
        supabase.from('schedule_entries').select('id, assigned_to, start_at, end_at, title')
          .eq('business_id', businessId).gte('start_at', `${today}T00:00:00`)
          .lte('start_at', `${today}T23:59:59`).abortSignal(ctrl.signal),
        // Also fetch today's completed count
      ]);
      const completedRes = await supabase.from('work_orders').select('id', { count: 'exact', head: true })
        .eq('business_id', businessId).eq('status', 'completed')
        .gte('completed_at', `${today}T00:00:00`).abortSignal(ctrl.signal);
      if (jobsRes.error) throw jobsRes.error;

      const uMap = new Map((usersRes.data ?? []).map(u => [u.id, u]));
      const jobs = (jobsRes.data ?? []) as DispatchJob[];

      // Enrich jobs with assigned user info
      for (const j of jobs) {
        if (j.assigned_id) {
          const u = uMap.get(j.assigned_id);
          if (u) { j.assigned_name = `${u.first_name} ${u.last_name}`; j.assigned_phone = u.phone ?? undefined; }
        }
      }

      // Split queue vs active
      const queueJobs = jobs.filter(j => !j.assigned_id || j.status === 'new');
      const activeJobs = jobs.filter(j => j.assigned_id && ['assigned', 'en_route', 'on_site'].includes(j.status));

      // Derive crew status
      const activeJobMap = new Map<string, DispatchJob>();
      for (const j of activeJobs) { if (j.assigned_id) activeJobMap.set(j.assigned_id, j); }
      const schedEntries = schedRes.data ?? [];
      const scheduledUserIds = new Set<string>();
      for (const se of schedEntries) {
        for (const uid of se.assigned_to ?? []) scheduledUserIds.add(uid);
      }

      // Derive crew status with strict priority: on_site > en_route > dispatched > scheduled > available > off
      const crewList: CrewMember[] = (usersRes.data ?? []).map(u => {
        const activeJob = activeJobMap.get(u.id);
        let crewStatus: string;
        let currentJobTitle: string | undefined;

        if (activeJob?.status === 'on_site') {
          crewStatus = 'on_job';
          currentJobTitle = activeJob.title;
        } else if (activeJob?.status === 'en_route') {
          crewStatus = 'en_route';
          currentJobTitle = activeJob.title;
        } else if (activeJob?.status === 'assigned') {
          crewStatus = 'scheduled';
          currentJobTitle = activeJob.title;
        } else if (scheduledUserIds.has(u.id)) {
          crewStatus = 'available'; // scheduled today but no active job = available for dispatch
        } else {
          crewStatus = 'off_today';
        }

        return { ...u, crewStatus, currentJobTitle };
      });

      if (mountedRef.current) {
        setQueue(queueJobs); setActive(activeJobs);
        setCompletedToday(completedRes.count ?? 0); setCrew(crewList);
      }
    } catch (e) { if (mountedRef.current) setError(e instanceof Error ? e.message : 'Failed'); }
    finally { clearTimeout(t); if (mountedRef.current) setLoading(false); }
  }, [businessId, today]);

  useEffect(() => { mountedRef.current = true; void fetch(); return () => { mountedRef.current = false; }; }, [fetch]);
  const rt = useMemo(() => ['work_orders', 'schedule_entries'], []);
  useRealtimeSubscription(businessId, rt, fetch);

  const summary = useMemo<DispatchSummary>(() => ({
    queueCount: queue.length,
    activeCount: active.length,
    completedCount: completedToday,
    crewAvailable: crew.filter(c => c.crewStatus === 'available').length,
    crewTotal: crew.length,
  }), [queue, active, completedToday, crew]);

  // ── Actions ──
  // Note: `assigned_type: 'employee'` literal removed (Foundation v1 §B5 / gap 0.7
  // closure). The bookings/work_orders.assigned_type CHECK already accepts
  // 'employee'|'contractor'|'operator'; Phase C UI surfaces the role picker.
  // For now, leave assigned_type unset (caller-supplied or NULL) and let downstream
  // flows handle it.
  const assignJob = useCallback(async (jobId: string, userId: string, dispatcherId: string) => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('work_orders').update({ assigned_id: userId, status: 'assigned' }).eq('id', jobId).eq('business_id', businessId);
      if (e) return false;

      const entryId = await findEntryIdForJob(jobId, businessId);
      if (entryId) {
        const reassignRes = await reassignEntry({
          entryId,
          newAssigneeId: userId,
          idempotencyKey: crypto.randomUUID(),
        });
        if (!reassignRes.success) {
          logger.warn('reassign_schedule_entry failed during dispatch:', entryId, lookupErrorCopy('reassign_schedule_entry', reassignRes.error.code));
        }
      } else {
        // Unscheduled job — no schedule_entry to reassign. Send dispatch notification
        // directly since the canonical RPC path doesn't run. Once a recurrence-aware
        // job-scheduling RPC ships, this fallback retires.
        try { await supabase.from('pending_notifications').insert({ business_id: businessId, notification_subtype: 'dispatch', recipient_id: userId, entity_type: 'work_order', entity_id: jobId, status: 'pending', attempts: 0 }); } catch { /* */ }
      }

      await log(businessId, 'job_dispatched', 'Job dispatched to crew', jobId, dispatcherId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch, reassignEntry]);

  const advanceStatus = useCallback(async (jobId: string, newStatus: string, dispatcherId: string) => {
    if (!businessId) return false;
    try {
      const updates: Record<string, unknown> = { status: newStatus };
      if (newStatus === 'on_site') updates.started_at = new Date().toISOString();
      if (newStatus === 'completed') updates.completed_at = new Date().toISOString();
      if (newStatus === 'en_route') updates.en_route_at = new Date().toISOString();
      const { error: e } = await supabase.from('work_orders').update(updates).eq('id', jobId).eq('business_id', businessId);
      if (e) return false;
      await log(businessId, `job_${newStatus}`, `Job status → ${newStatus}`, jobId, dispatcherId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  const reassignJob = useCallback(async (jobId: string, newUserId: string, dispatcherId: string) => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('work_orders').update({ assigned_id: newUserId }).eq('id', jobId).eq('business_id', businessId);
      if (e) return false;

      const entryId = await findEntryIdForJob(jobId, businessId);
      if (entryId) {
        const reassignRes = await reassignEntry({
          entryId,
          newAssigneeId: newUserId,
          idempotencyKey: crypto.randomUUID(),
        });
        if (!reassignRes.success) {
          logger.warn('reassign_schedule_entry failed during reassign:', entryId, lookupErrorCopy('reassign_schedule_entry', reassignRes.error.code));
        }
      } else {
        // Unscheduled job — see assignJob comment.
        try { await supabase.from('pending_notifications').insert({ business_id: businessId, notification_subtype: 'reassign', recipient_id: newUserId, entity_type: 'work_order', entity_id: jobId, status: 'pending', attempts: 0 }); } catch { /* */ }
      }

      await log(businessId, 'job_reassigned', 'Job reassigned', jobId, dispatcherId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch, reassignEntry]);

  const cancelDispatch = useCallback(async (jobId: string, dispatcherId: string) => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('work_orders').update({ status: 'cancelled' }).eq('id', jobId).eq('business_id', businessId);
      if (e) return false;
      await log(businessId, 'job_cancelled', 'Dispatch cancelled', jobId, dispatcherId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  return { queue, active, crew, loading, error, summary, assignJob, advanceStatus, reassignJob, cancelDispatch, refresh: fetch };
}
